import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox,
  CircularProgress, Alert, IconButton, Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { updateLeadContact } from '../services/api';

const SENIORITY = ['Unknown', 'Executive', 'Director', 'Manager', 'Individual'];

export default function ContactEditDialog({ open, leadId, contact, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && contact) {
      setForm({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        email: contact.email || '',
        phone: contact.phone || '',
        jobTitle: contact.jobTitle || '',
        jobTitleConfirmed: !!contact.jobTitleConfirmed,
        linkedInUrl: contact.linkedInUrl || '',
        department: contact.department || '',
        seniorityLevel: contact.seniorityLevel || 'Unknown',
        isPrimary: !!contact.isPrimary,
        notes: contact.notes || '',
      });
      setError('');
    }
  }, [open, contact]);

  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        jobTitle: form.jobTitle,
        jobTitleConfirmed: form.jobTitleConfirmed,
        linkedInUrl: form.linkedInUrl,
        department: form.department,
        seniorityLevel: form.seniorityLevel,
        isPrimary: form.isPrimary,
        notes: form.notes,
      };
      const { data } = await updateLeadContact(leadId, contact.id, payload);
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Edit contact
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{String(error)}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" label="First name" fullWidth value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
            <TextField size="small" label="Last name" fullWidth value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField size="small" label="Job title" fullWidth value={form.jobTitle} onChange={(e) => set('jobTitle', e.target.value)} />
            <FormControlLabel
              control={<Checkbox checked={form.jobTitleConfirmed} onChange={(e) => set('jobTitleConfirmed', e.target.checked)} />}
              label="Confirmed"
              sx={{ whiteSpace: 'nowrap' }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" label="Department" fullWidth value={form.department} onChange={(e) => set('department', e.target.value)} />
            <FormControl size="small" fullWidth>
              <InputLabel>Seniority</InputLabel>
              <Select label="Seniority" value={form.seniorityLevel} onChange={(e) => set('seniorityLevel', e.target.value)}>
                {SENIORITY.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" label="Email" type="email" fullWidth value={form.email} onChange={(e) => set('email', e.target.value)} />
            <TextField size="small" label="Phone" fullWidth value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </Stack>
          <TextField size="small" label="LinkedIn URL" fullWidth value={form.linkedInUrl} onChange={(e) => set('linkedInUrl', e.target.value)} />
          <FormControlLabel
            control={<Checkbox checked={form.isPrimary} onChange={(e) => set('isPrimary', e.target.checked)} />}
            label="Primary contact for this lead"
          />
          <TextField size="small" label="Notes" fullWidth multiline minRows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Box sx={{ position: 'relative' }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          {saving && <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-10px', marginLeft: '-10px' }} />}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
